import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useLocale } from "@/context/LocaleContext";

export default function Home() {
  const { t, lang } = useLocale();
  const location = useLocation();

  const isEnglish = lang === "en";
  const enTitle =
    "BuildStore: E-commerce Development | Online Store. Turnkey Solution.";
  const itTitle =
    "BuildStore: Sviluppo E-commerce | Store Online. Soluzione Chiavi in Mano.";
  const enDesc =
    "Launch your 100% owned online store starting from €699. Zero subscriptions, zero commissions: your definitive platform, yours forever. High-performance infrastructure optimized for sales. Stop paying rent for your business, own your future.";
  const itDesc =
    "Lancia il tuo store online 100% proprietario a partire da 699€. Zero abbonamenti, zero commissioni: la tua piattaforma definitiva, tua per sempre. Infrastruttura impeccabile ottimizzata per le vendite. Smetti di pagare l'affitto per il tuo business, investi nel tuo futuro.";

  const canonicalUrl =
    typeof window !== "undefined"
      ? window.location.origin + (location.pathname || "/")
      : undefined;

  return (
    <>
      <Helmet>
        <title>{isEnglish ? enTitle : itTitle}</title>
        <meta name="description" content={isEnglish ? enDesc : itDesc} />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:title" content={isEnglish ? enTitle : itTitle} />
        <meta property="og:description" content={isEnglish ? enDesc : itDesc} />
        <meta
          property="og:image"
          content="https://www.buildstore.it/logo-buildstore-v53.png"
        />
        <meta property="og:type" content="website" />
      </Helmet>
      <main className="min-h-screen bg-[radial-gradient(ellipse_at_top,_rgba(16,185,129,0.08),transparent_50%),radial-gradient(ellipse_at_bottom,_rgba(59,130,246,0.06),transparent_50%)] pt-56">
        <section className="container mx-auto pt-12 pb-24 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
            <span className="block">{t("home.h1")}</span>
          </h1>
          <p className="mt-4 text-white/70 max-w-3xl mx-auto text-lg">
            {t("home.p")}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <a
              href="/pricing#pricing"
              className="inline-flex flex-col items-center justify-center rounded-lg bg-primary text-primary-foreground px-8 py-4 font-semibold shadow-md gap-1 hover:bg-primary/90 transition-colors"
            >
              <span className="text-base md:text-lg">
                {t("home.book_deploy")}
              </span>
              <span className="text-xs md:text-sm font-normal opacity-90">
                {t("home.book_deploy_sub")}
              </span>
            </a>
            <Link
              to="/products"
              className="inline-block rounded-lg border border-white/10 px-6 py-3 text-white/80"
            >
              {t("home.view_products")}
            </Link>
            <a
              href={`https://wa.me/393505933542?text=${encodeURIComponent(t("whatsapp.message"))}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block rounded-lg border border-white/10 px-6 py-3 text-white/80"
            >
              {t("whatsapp.label")}
            </a>
          </div>

          <div className="mt-12 mx-auto max-w-5xl grid gap-8 md:grid-cols-2 items-center px-4">
            <div className="text-left">
              <p className="text-sm uppercase tracking-[0.2em] text-primary mb-2">
                {t("home.portfolio_label")}
              </p>
              <p className="text-white/80 text-base md:text-lg mb-3">
                {t("home.portfolio_projects")}
              </p>
              <p className="text-white/60 text-sm">
                {t("home.portfolio_details")}
              </p>
            </div>
            <div className="relative w-full">
              <div className="absolute inset-0 -translate-x-6 -translate-y-6 rounded-3xl bg-gradient-to-tr from-emerald-500/30 via-sky-500/30 to-purple-500/20 blur-3xl" />
              <div className="relative rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-4 md:p-5 shadow-[0_20px_80px_rgba(15,23,42,0.9)] overflow-hidden">
                <div className="flex items-center justify-between mb-4">
                  <div className="h-8 w-24 rounded-full bg-white/10 flex-shrink-0" />
                  <div className="flex gap-2 flex-shrink-0">
                    <span className="h-2 w-2 rounded-full bg-emerald-400" />
                    <span className="h-2 w-2 rounded-full bg-amber-400" />
                    <span className="h-2 w-2 rounded-full bg-rose-400" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="col-span-2 h-16 md:h-24 rounded-xl bg-black/40 border border-white/10" />
                  <div className="h-16 md:h-24 rounded-xl bg-black/30 border border-white/10" />
                </div>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="h-12 md:h-16 rounded-lg bg-black/40 border border-white/10" />
                  <div className="h-12 md:h-16 rounded-lg bg-black/30 border border-white/10" />
                  <div className="h-12 md:h-16 rounded-lg bg-black/30 border border-white/10" />
                </div>
                <div className="mt-4 flex items-center justify-between gap-2">
                  <div className="space-y-1 flex-shrink-0">
                    <div className="h-2 md:h-3 w-16 md:w-24 rounded-full bg-white/40" />
                    <div className="h-2 md:h-3 w-24 md:w-40 rounded-full bg-white/20" />
                  </div>
                  <div className="flex gap-2 items-center flex-shrink-0">
                    <div className="h-10 px-4 md:px-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold whitespace-nowrap flex-shrink-0">
                      A partire da €699
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="container mx-auto py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 bg-white/4 rounded-lg border border-white/8">
              <h4 className="text-white font-semibold">
                {t("feature.performance.title")}
              </h4>
              <p className="text-white/70 mt-2">
                {t("feature.performance.desc")}
              </p>
            </div>
            <div className="p-6 bg-white/4 rounded-lg border border-white/8">
              <h4 className="text-white font-semibold">
                {t("feature.security.title")}
              </h4>
              <p className="text-white/70 mt-2">{t("feature.security.desc")}</p>
            </div>
            <div className="p-6 bg-white/4 rounded-lg border border-white/8">
              <h4 className="text-white font-semibold">
                {t("feature.integrations.title")}
              </h4>
              <p className="text-white/70 mt-2">
                {t("feature.integrations.desc")}
              </p>
            </div>
          </div>
        </section>

        <section className="container mx-auto py-12">
          <div className="bg-gradient-to-r from-emerald-500/10 via-sky-500/10 to-purple-500/10 border border-white/10 rounded-lg backdrop-blur-xl p-8 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
              {t("packages.title")}
            </h3>
            <p className="text-white/70 mb-4 max-w-2xl mx-auto">
              {t("packages.subtitle")}
            </p>
            <p className="text-white/70 mb-6 max-w-2xl mx-auto">
              {t("packages.long_description")}
            </p>
            <a
              href="/pricing#pricing"
              className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              {t("packages.open")}
            </a>
          </div>
        </section>

        <section className="container mx-auto py-12">
          <div className="bg-gradient-to-r from-emerald-500/10 via-sky-500/10 to-purple-500/10 border border-white/10 rounded-lg backdrop-blur-xl p-8 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
              {t("home.why_buildstore_title")}
            </h3>
            <p className="text-white/70 mb-6 max-w-2xl mx-auto">
              {t("home.why_buildstore_desc")}
            </p>
            <a
              href="/valore"
              className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              {t("home.read_comparison")}
            </a>
          </div>
        </section>
      </main>
    </>
  );
}
